import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { KeyRound, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { ClientBadge, LevelBadge } from "../../components/Badges";
import { PasswordStrength } from "../../components/PasswordStrength";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../context/AuthContext";
import { api, apiError, mediaUrl } from "../../lib/api";
import { formatDate, formatDateTime, initials } from "../../lib/format";

const schema = z
  .object({
    current_password: z.string().min(1, "Enter your current password"),
    new_password: z
      .string()
      .min(8, "At least 8 characters")
      .regex(/[A-Z]/, "Add an uppercase letter")
      .regex(/[a-z]/, "Add a lowercase letter")
      .regex(/[0-9]/, "Add a number")
      .regex(/[^A-Za-z0-9]/, "Add a special character"),
    confirm: z.string().min(1, "Confirm the new password"),
  })
  .refine((v) => v.new_password === v.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

const Row = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 border-b border-border py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-sm">{value || "Not provided"}</span>
  </div>
);

export default function Profile() {
  const { user } = useAuth();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
  });
  const newPassword = watch("new_password");
  const photo = mediaUrl(user?.photo);

  const onSubmit = async (values) => {
    try {
      await api.post("/api/auth/change-password", {
        current_password: values.current_password,
        new_password: values.new_password,
      });
      toast.success("Password changed successfully");
      reset();
    } catch (error) {
      toast.error(apiError(error, "Could not change the password"));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="My profile" subtitle="Your account details and security settings." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Account details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 pb-4">
              <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-base font-semibold">
                {photo ? (
                  <img src={photo} alt={user?.name} className="h-full w-full object-cover" />
                ) : (
                  initials(user?.name)
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-semibold">{user?.name}</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <LevelBadge level={user?.level} />
                  {(user?.badges || []).map((b) => (
                    <ClientBadge key={b} label={b} />
                  ))}
                </div>
              </div>
            </div>
            <Row label="Email" value={user?.email} />
            <Row label="Phone" value={user?.phone} />
            <Row label="Address" value={user?.address} />
            <Row label="Relationship manager" value={user?.manager?.name} />
            <Row label="Member since" value={formatDate(user?.createdAt)} />
            <Row label="Last sign-in" value={formatDateTime(user?.lastLoginAt)} />
            <p className="mt-4 flex items-start gap-2 rounded-lg border border-border surface-2 p-3 text-xs text-muted-foreground">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Profile and financial details are maintained by Invextiing. To update your contact
              information, please contact your relationship manager.
            </p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Change password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="current_password">Current password</Label>
                <Input
                  id="current_password"
                  type="password"
                  autoComplete="current-password"
                  {...register("current_password")}
                  data-testid="change-password-current-input"
                />
                {errors.current_password ? (
                  <p className="text-xs text-destructive">{errors.current_password.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New password</Label>
                <Input
                  id="new_password"
                  type="password"
                  autoComplete="new-password"
                  {...register("new_password")}
                  data-testid="change-password-new-input"
                />
                {errors.new_password ? (
                  <p className="text-xs text-destructive">{errors.new_password.message}</p>
                ) : null}
                <PasswordStrength value={newPassword} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  {...register("confirm")}
                  data-testid="change-password-confirm-input"
                />
                {errors.confirm ? (
                  <p className="text-xs text-destructive">{errors.confirm.message}</p>
                ) : null}
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full"
                data-testid="change-password-submit-button"
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="mr-2 h-4 w-4" />
                )}
                Update password
              </Button>
              <p className="flex items-start gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--profit))]" />
                Sessions expire automatically after a period of inactivity for your protection.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
